import { WarningCircleIcon } from '@phosphor-icons/react';
import React from 'react';

interface BaseInputProps extends React.ComponentProps<'input'> {
  leftIcon?: React.ReactNode;
  disabled?: boolean;
  placeholder: string;
  helperText?: string;
  error?: string;
  inputSize?: 'sm' | 'md' | 'lg';
}

export function BaseInput({
  leftIcon,
  disabled,
  placeholder,
  helperText,
  error,
  inputSize = 'md',
  ...props
}: BaseInputProps) {
  const sizeClasses = {
    sm: 'px-2.5 py-1.5 text-sm',
    md: 'px-3.5 py-2.5 text-base',
    lg: 'px-4 py-3 text-lg',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const gapClasses = {
    sm: 'gap-1.5',
    md: 'gap-2',
    lg: 'gap-2.5',
  };

  return (
    <div className="self-stretch inline-flex flex-col justify-start items-start gap-1.5">
      <div
        className={`self-stretch ${sizeClasses[inputSize]} rounded-lg outline-1 outline-offset-[-1px] inline-flex justify-start items-center ${gapClasses[inputSize]} overflow-hidden input-shadow transition-colors ${
          disabled ? 'bg-gray-50' : 'bg-white'
        } ${
          error
            ? 'input-error outline-error-300 focus-within:outline-error-300'
            : 'outline-gray-300 focus-within:outline-blue-300'
        }`}
      >
        <div className={`flex-1 flex justify-start items-center ${gapClasses[inputSize]}`}>
          {leftIcon && (
            <div className={`text-gray-500 ${iconSizeClasses[inputSize]}`}>{leftIcon}</div>
          )}
          <input
            className={`flex-1 justify-start placeholder:text-gray-500 text-gray-900 ${sizeClasses[inputSize].split(' ')[2]} font-normal leading-normal outline-none`}
            placeholder={placeholder}
            type="text"
            {...props}
          />
          {error && (
            <WarningCircleIcon className={`text-error-500 ${iconSizeClasses[inputSize]}`} />
          )}
        </div>
      </div>
      {error ? (
        <div className="self-stretch justify-start text-error-500 text-xs font-normal leading-none">
          {error}
        </div>
      ) : (
        <div className="self-stretch justify-start text-gray-600 text-xs font-normal leading-none">
          {helperText}
        </div>
      )}
    </div>
  );
}
